<script setup>
import { useStore } from 'vuex'
import router from '@/router'
import { EaseChatSDK, EaseChatClient } from '@/IM/initwebsdk'
import { handleSDKErrorNotifi, setMessageKey } from '@/utils/handleSomeData'
import { informType } from '@/constant'
import { usePlayRing } from '@/hooks'
import ring from '@/assets/ring.mp3'
import {
    captchaImage,
    loginCustom,
    getImToken,
    getUserInfo
} from '@/api/custom'
/* callkit */
import EaseCallKit from '@/components/EaseCallKit'
import InviteCallMembers from '@/components/InviteCallMembers'
import { ElLoading } from 'element-plus'
import { ref, onMounted, onUnmounted } from 'vue'
const store = useStore()
const { isOpenPlayRing, clickRing } = usePlayRing()
EaseChatSDK.logger.disableAll()
let isPageHidden = false
let isClosingForPageHide = false
let shouldRestoreConnection = false
let isRestoringConnection = false
let connectedUser = ''

/** @returns {{ user: string, accessToken: string } | null} */
const getStoredLoginUser = () => {
    try {
        const loginUser = JSON.parse(
            window.localStorage.getItem('EASEIM_loginUser')
        )
        if (
            typeof loginUser?.user === 'string' &&
            loginUser.user.trim() &&
            typeof loginUser.accessToken === 'string' &&
            loginUser.accessToken.trim()
        ) {
            return {
                user: loginUser.user.toLowerCase(),
                accessToken: loginUser.accessToken
            }
        }
    } catch {
        // 无效缓存不参与自动登录，也不回退到其他账号遗留的 Token。
    }
    return null
}

const isChatPage = () => router.currentRoute.value.path.startsWith('/chat/')
const restoreConnection = async () => {
    if (
        isPageHidden ||
        isClosingForPageHide ||
        isRestoringConnection ||
        EaseChatClient.isOpened() ||
        !isChatPage() ||
        !navigator.onLine
    )
        return

    const loginUser = getStoredLoginUser()
    if (!loginUser) {
        shouldRestoreConnection = false
        router.replace('/login')
        return
    }

    isRestoringConnection = true
    try {
        // 页面被冻结期间 Token 可能已经过期，恢复时从业务服务器重新获取。
        const response = await getImToken()
        const currentLoginUser = getStoredLoginUser()
        if (
            isPageHidden ||
            !isChatPage() ||
            currentLoginUser?.accessToken !== loginUser.accessToken ||
            currentLoginUser?.user !== loginUser.user
        ) {
            isRestoringConnection = false
            return
        }
        if (typeof response?.msg !== 'string' || !response.msg.trim()) {
            throw new Error('获取聊天登录凭据失败，请重新登录')
        }
        const credentials = { user: loginUser.user, accessToken: response.msg }
        window.localStorage.setItem(
            'EASEIM_loginUser',
            JSON.stringify(credentials)
        )
        // open() 返回不代表长连接已经建立，由 onConnected 结束恢复状态。
        await EaseChatClient.open(credentials)
    } catch (error) {
        isRestoringConnection = false
        if (!isPageHidden && isChatPage()) {
            handleSDKErrorNotifi(
                error?.type,
                error?.message || '聊天连接恢复失败，请检查网络或重新登录'
            )
        }
    }
}

const handlePageHide = () => {
    isPageHidden = true
    shouldRestoreConnection = isChatPage() && !!getStoredLoginUser()
    if (shouldRestoreConnection && EaseChatClient.isOpened()) {
        isClosingForPageHide = true
        EaseChatClient.close()
    }
}
/** @param {PageTransitionEvent} event */
const handlePageShow = (event) => {
    isPageHidden = false
    if (event.persisted && shouldRestoreConnection) {
        restoreConnection()
    }
}
onMounted(() => {
    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('pageshow', handlePageShow)
    // 自己服务器做个心跳包，定时请求接口，避免token超时
    clearInterval()
    setInterval(() => {
        if (router.currentRoute.value.path != '/login') {
            getUserInfo()
        }
    }, 5 * 60 * 1000)

    store.dispatch('getLocalUnreadMsgIds')
})
onUnmounted(() => {
    isPageHidden = true
    window.removeEventListener('pagehide', handlePageHide)
    window.removeEventListener('pageshow', handlePageShow)
    EaseChatClient.removeEventHandler('connection')
    clearInterval()
})
/* connect 相关监听 */
EaseChatClient.addEventHandler('connection', {
    onConnected: () => {
        isRestoringConnection = false
        if (isPageHidden) {
            shouldRestoreConnection = true
            isClosingForPageHide = true
            EaseChatClient.close()
            return
        }
        const isReconnection =
            connectedUser === EaseChatClient.user && isChatPage()
        connectedUser = EaseChatClient.user
        shouldRestoreConnection = false
        //console.log('>>>>>环信连接成功')
        // 关闭时通知父窗口
        //
        // if(window.opener&&!window.opener.closed){
        //   // window.opener.windowMessage
        //   // 关闭本窗口时进行提示
        //   window.addEventListener('beforeunload', function(event) {
        //     window.opener.postMessage('closed','https://erp.yududs.com')
        //     window.opener.postMessage('closed','http://yudu.yududs.com')
        //     window.opener.postMessage('closed','http://192.168.3.101')
        //     EaseChatClient.close()
        //   });
        // }
        store.commit('CHANGE_LOGIN_STATUS', true)
        if (!isReconnection && isOpenPlayRing.value) clickRing()
        fetchLoginUsersInitData(!isReconnection)
        if (isReconnection) return
        const show = window.sessionStorage.getItem('showNav')
        //console.log('%c Line:26 🍅 show', 'color:#33a5ff', show)
        // if(show) show = JSON.stringify(show)
        router.replace({
            path: '/chat/conversation',
            query: { showNav: show ? show : true }
        })
    },
    onDisconnected: () => {
        store.commit('CHANGE_LOGIN_STATUS', false)
        if (isClosingForPageHide) {
            isClosingForPageHide = false
            isRestoringConnection = false
            // close 回调可能在 pageshow 前后到达，确认旧连接关闭后才能重连。
            if (!isPageHidden && shouldRestoreConnection) {
                window.setTimeout(restoreConnection, 0)
            }
            return
        }
        if (isPageHidden || !isChatPage() || !getStoredLoginUser()) {
            isRestoringConnection = false
            return
        }
        shouldRestoreConnection = true
        if (isRestoringConnection) {
            isRestoringConnection = false
            handleSDKErrorNotifi(
                undefined,
                '聊天连接恢复失败，请检查网络或重新登录'
            )
            return
        }
        window.setTimeout(restoreConnection, 0)
    },
    onOnline: () => {
        store.commit('CHANGE_NETWORK_STATUS', true)
        if (shouldRestoreConnection) restoreConnection()
    }, // 本机网络连接成功。
    onOffline: () => {
        store.commit('CHANGE_NETWORK_STATUS', false)
    }, // 本机网络掉线。
    onError: (error) => {
        if (isPageHidden || isClosingForPageHide) return
        //console.log('on error', error)
        handleSDKErrorNotifi(error.type, error.message)
    }
})
//路由跳转-对应好友会话
const toChatMessage = (id, chatType, avatarurl) => {
    // //console.log('>>>>>>>id', id)
    router.push({
        path: '/chat/conversation/message',
        query: {
            id,
            chatType,
            avatarurl
        }
    })
}
//fetch 登陆用户的初始数据
const fetchLoginUsersInitData = (resetConversation = true) => {
    getMyUserInfos()
    fetchFriendList()
    fetchTheLoginUserBlickList()
    fetchGroupList()
    //初始化vuex中的会话列表相关数据
    if (resetConversation) store.commit('INIT_CONVERSATION_STATE')
    const userId = EaseChatClient.user
    store.dispatch('getSessionList', userId)
}
//获取登陆用户属性
const getMyUserInfos = () => {
    const userId = EaseChatClient.user
    store.dispatch('getMyUserInfo', userId)
}
//获取好友列表
const fetchFriendList = () => {
    // const { value = {} } = useLocalStorage('friendList')
    // if (Object.values(JSON.parse(value)).length > 0) return
    store.dispatch('fetchFriendList')
}
//获取黑名单列表
const fetchTheLoginUserBlickList = () => store.dispatch('fetchBlackList')
//获取加入的群组列表
const fetchGroupList = () => {
    //如果本地存储里不存在群组列表则调用获取群组列表
    // const { value = {} } = useLocalStorage('groupList')
    // if (Object.values(JSON.parse(value)).length > 0) return
    const pageParams = {
        pageNum: 1,
        pageSize: 600
    }
    store.dispatch('fetchGroupList', pageParams)
}
//在线状态订阅相关
const presenceStatus = (type, user) => {
    type === 'sub' && store.dispatch('subFriendsPresence', [user])
    type === 'unsub' && store.dispatch('unsubFriendsPresence', [user])
}

/* presence 相关监听 */
EaseChatClient.addEventHandler('presenceStatusChange', {
    onPresenceStatusChange: (status) => {
        //console.log('>>>>>presenceStatusChange', status)
        getUserPresence(...status)
    }
})
//处理登陆用户状态的变更
const getUserPresence = (status) => {
    store.dispatch('handlePresenceChanges', status)
}
/* message 相关监听 */
EaseChatClient.addEventHandler('messageListen', {
    onTextMessage: function (message) {
        //console.log('>>>>>>>App mesage', message)
        //console.log('setMessageKey', setMessageKey(message))
        pushNewMessage(message)
    }, // 收到文本消息。
    onEmojiMessage: function (message) {
        pushNewMessage(message)
    }, // 收到表情消息。
    onImageMessage: function (message) {
        //console.log('>>>>>>>App Image mesage', message)
        pushNewMessage(message)
    }, // 收到图片消息。
    onCmdMessage: function (message) {
        //console.log('>>>>>收到命令消息', message)
    }, // 收到命令消息。
    onAudioMessage: function (message) {
        pushNewMessage(message)
    }, // 收到音频消息。
    onLocationMessage: function (message) {
        pushNewMessage(message)
    }, // 收到位置消息。
    onFileMessage: function (message) {
        pushNewMessage(message)
    }, // 收到文件消息。
    onCustomMessage: function (message) {
        console.log('>>>>>自定义消息', message)
        pushNewMessage(message)
    }, // 收到自定义消息。
    onVideoMessage: function (message) {
        pushNewMessage(message)
    }, // 收到视频消息。
    onRecallMessage: function (message) {
        otherRecallMessage(message)
    }, // 收到消息撤回回执。

    onReadMessage: function (message) {
        if (message.from != EaseChatClient.user) {
            store.commit('CHANGE_MESSAGE_BODAY', {
                type: 'read',
                key: message.from,
                mid: message.mid
            })
        } else {
        }
    },
    onChannelMessage: (message) => {
        if (message.chatType == 'singleChat') {
            // 将这个会话下所有的消息都变为已读
            store.commit('CHANGE_MESSAGE_BODAY', {
                type: 'converReaded',
                key: message.from
            })
        }
    }
    // onDeliveredMessage: function (message) {
    //   console.log("%c Line:200 🍻 消息已送达回执message", "color:#fca650", message);

    // },
})
//接收的消息往store中push
const pushNewMessage = (message) => {
    // 收到消息的时候如果在私聊的会话页面，直接发送已读回执
    const route = router.currentRoute.value
    if (
        route.path == '/chat/conversation/message' &&
        route.query.id == message.from
    ) {
        // 私聊会话，并且在当前会话页面
        const option = {
            type: 'read', // 消息是否已读。
            chatType: 'singleChat', // 会话类型，这里为单聊。
            to: message.from, // 消息接收方的用户 ID。
            id: message.id // 需要发送已读回执的消息 ID。
        }
        const msg = EaseChatSDK.message.create(option)
        EaseChatClient.send(msg)
    }
    store.dispatch('createNewMessage', message)
}
//收到他人的撤回指令
const otherRecallMessage = (message) => {
    //console.log('>>>>>收到他人撤回', message)
    const { from, to, mid } = message
    //单对单的撤回to必然为登陆的用户id，群组发起撤回to必然为群组id 所以key可以这样来区分群组或者单人。
    const key = to === EaseChatClient.user ? from : to
    //console.log('>>>>>收到他人撤回', key)
    store.commit('CHANGE_MESSAGE_BODAY', { type: 'recall', key, mid })
    store.dispatch('gatherConversation', key)
}
/* 好友关系相关监听 */
const { INFORM_FROM } = informType
EaseChatClient.addEventHandler('friendListen', {
    // 收到好友邀请触发此方法。
    onContactInvited: (data) => {
        //写入INFORM
        //console.log('>>>>>>收到好友申请', data)
        submitInformData(INFORM_FROM.FRIEND, data)
    },
    // 联系人被删除时触发此方法。
    onContactDeleted: (data) => {
        //写入INFORM
        //console.log('>>>>收到好友关系解散', data)
        submitInformData(INFORM_FROM.FRIEND, data)
        //取消针对好友的在线状态订阅
        presenceStatus('unsub', data.from)
        //好友关系解除重新获取好友列表
        fetchFriendList()
    },
    // 新增联系人会触发此方法。
    onContactAdded: (data) => {
        //console.log('>>>>好友新增监听', data)
        submitInformData(INFORM_FROM.FRIEND, data)
        //新增好友重新获取好友列表
        fetchFriendList()
    },
    // 好友请求被拒绝时触发此方法。
    onContactRefuse: (data) => {
        //写入INFORM
        //console.log('>>>>>>好友申请被拒绝', data)
        data.type = 'other_person_refuse'
        submitInformData(INFORM_FROM.FRIEND, data)
    },
    // 好友请求被同意时触发此方法。
    onContactAgreed: (data) => {
        //写入INFORM
        //console.log('>>>>>对方同意了好友申请', data)
        //改掉data中的type
        data.type = 'other_person_agree'
        submitInformData(INFORM_FROM.FRIEND, data)
        //对方同意后重新获取好友列表
        fetchFriendList()
    }
})
/* 群组相关监听 */
EaseChatClient.addEventHandler('groupEvent', {
    onGroupEvent: (groupevent) => {
        //console.log('>>>>>>>收到群组事件', groupevent)
        submitInformData(INFORM_FROM.GROUP, groupevent)
    }
})

const submitInformData = (fromType, informContent) => {
    //console.log('>>>submitInformData>>>', fromType, informContent)
    store.dispatch('createNewInform', { fromType, informContent })
}

/* 重新登陆 */
//读取本地EASEIM_loginUser
const loginUserFromStorage = getStoredLoginUser()
const handleRelogin = () => {
    //console.log('重新登陆')
    EaseChatClient.open({
        user: loginUserFromStorage.user,
        accessToken: loginUserFromStorage.accessToken
    }).catch((error) => {
        handleSDKErrorNotifi(
            error?.type,
            error?.message || '聊天登录失败，请重新登录'
        )
    })
}
if (loginUserFromStorage?.user && loginUserFromStorage?.accessToken) {
    handleRelogin()
}
/* EaseCallKit 相关 */
const easeCallKit = ref(null)
const inviteCallComp = ref(null)
//多人会议使用-弹出邀请模态框
const showModal = ({ groupId }) => {
    //console.log('可以弹出邀请框', groupId)
    inviteCallComp.value.alertDialog(groupId)
}
//多人会议使用-传递给邀请组件发送邀请消息
const sendMulitInviteMsg = (targetIMId) => {
    //console.log('targetIMIdtargetIMIdtargetIMId', targetIMId)
    const callType = 2
    easeCallKit.value.inMultiChanelSendInviteMsg(targetIMId, callType)
}
</script>
<template>
    <router-view v-slot="{ Component }">
        <transition
            name="slide-fade"
            mode="out-in"
            :duration="{ enter: 500, leave: 300 }"
        >
            <component :is="Component" />
        </transition>
    </router-view>
    <!-- 铃声标签 -->
    <audio id="ring" :src="ring" controls hidden></audio>
    <!-- About EaseCallKit -->
    <EaseCallKit
        ref="easeCallKit"
        :EaseIMClient="EaseChatClient"
        :msgCreateFunc="EaseChatSDK.message"
        @onInviteMembers="showModal"
    />
    <InviteCallMembers
        ref="inviteCallComp"
        @sendMulitInviteMsg="sendMulitInviteMsg"
    />
</template>

<style type="scss">
@import './styles/reset/reset.css';
@import './styles/iconfont/iconfont.css';

/* .slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0.3;
}

.slide-fade-enter-to,
.slide-fade-leave-from {
  opacity: 1;
} */
.el-dialog__header {
    background: #f2f2f2;
    margin: 0 !important;
}
.el-dialog__body {
    padding: 10px !important;
    /* background-color: #f9f9f9; */
}
</style>
